import React from 'react';
import _merge from 'lodash/merge';
import { getContext, getEasing, getDuration, isAnimationDisabled } from 'common';
import { useSpring, animated } from 'react-spring';
import { SPEED_FAST } from '@nokia-csf-uxr/freeform-design-tokens/tokens/speed';
import { TRANSITION_DECELERATE } from '@nokia-csf-uxr/freeform-design-tokens/tokens/transition';
import Styled from './styles';
import { SignInContext } from './SignIn';

export interface SignInContentProps extends React.ComponentPropsWithRef<'div'> {
  /** Place content here. */
  children: React.ReactNode;
  /** Control animation config here, used in conjunction with `animateOut` prop of SignIn.  This uses React-spring config. See https://react-spring.io for more information. */
  animationProps?: any;
  /** If true, disable all motion animation for this component.  This property will override any global disableAnimation setting. */
  disableAnimation?: boolean;
  /** To customize the wrapper style. */
  style?: React.CSSProperties
}

/** SignInContent, place main SignIn content here. See sub-components such as SignInHeader, SignInBody, SignInFooter.  -  */
const SignInContent = React.forwardRef<HTMLElement, SignInContentProps>((props: SignInContentProps, ref) =>
  getContext(({ RTL, disableAnimation }) => {
    const { children, style, animationProps, disableAnimation: propsdisableAnimation, ...otherProps } = props;
    const { animateOut } = React.useContext(SignInContext);
    const resolvedDisableAnimation = isAnimationDisabled(propsdisableAnimation, disableAnimation);
    const springProps = useSpring(animationProps || {
      width: animateOut ? '100%' : '0%',
      delay: animateOut && 1000,
      config: {
        duration: getDuration(SPEED_FAST),
        easing: getEasing(TRANSITION_DECELERATE)
      },
      immediate: resolvedDisableAnimation
    });
    const resolvedStyles = _merge({}, resolvedDisableAnimation ? {} : springProps, style);
    return (
      <Styled.SignInContent $rtl={RTL} ref={ref} as={animated.div} style={resolvedStyles} {...otherProps}>
        {children}
      </Styled.SignInContent>
    );
  }));

SignInContent.defaultProps = {
  animationProps: undefined,
  disableAnimation: undefined,
  style: {}
};

SignInContent.displayName = 'SignInContent';

/* Exports */
export { SignInContent as default };
